import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, ChevronDown, ChevronUp, Play } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { ExportButton } from '@/components/common/ExportButton';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useRecordingLabel } from '@/hooks/useRecordingLabel';
import { ApiError } from '@/lib/api';
import { clock, findTerms, holdsTerms, splitOnTerms } from '@/lib/audio';
import { fetchCadeAudioTranscript } from '@/services/cade';
import type { CadeEventSpan, CadeTranscript } from '@/services/types';

/**
 * The whole machine transcript of one recording, in order, one segment per line with the
 * second it starts at and a button that plays the audio from there.
 *
 * Segments that a spoken-vote event was read from are highlighted and named, and a link with
 * `#seg-N` (from a search hit or an event on the recording page) opens the page scrolled to
 * that segment. It is a machine transcript: the notice sits above the text and travels in the
 * downloaded file too.
 */
export function CadeAudioTranscriptPage() {
  const { t } = useTranslation(['cade']);
  const { key = '' } = useParams();
  const label = useRecordingLabel();
  const location = useLocation();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cade', 'audio', 'transcript', key],
    queryFn: () => fetchCadeAudioTranscript(key),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  // Scroll to the segment the link names once the text exists to scroll to.
  useEffect(() => {
    if (!data || !location.hash.startsWith('#seg-')) return;
    document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: 'center' });
  }, [data, location.hash]);

  const play = (seconds: number) => {
    const element = audioRef.current;
    if (!element) return;
    element.currentTime = seconds;
    void element.play().catch(() => undefined);
  };

  if (isPending) {
    return (
      <div className="p-6">
        <SkeletonLoader rows={8} />
      </div>
    );
  }
  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="space-y-4 p-6">
        <BackLink to="/cade/audio" text={t('cade:audio.detail.back')} />
        <ErrorMessage
          message={notFound ? t('cade:audio.transcript.notFound') : error.message}
          onRetry={notFound ? undefined : () => void refetch()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <BackLink to={`/cade/audio/${key}`} text={t('cade:audio.transcript.back')} />
      <Answer answer={data.transcript} help={t('cade:audio.transcript.help')}>
        {(transcript: CadeTranscript) => (
          <>
            <header className="flex flex-wrap items-start justify-between gap-2">
              <div className="space-y-1">
                <h1 className="text-lg font-semibold text-foreground">
                  {label(transcript.recording)}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {t('cade:audio.transcript.subtitle')}
                </p>
              </div>
              <ExportButton
                path={`/api/v1/cade/export/audio/recordings/${encodeURIComponent(key)}/transcript`}
                label={t('cade:audio.transcript.download')}
              />
            </header>
            <p
              role="note"
              className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-foreground"
              data-machine-transcript
            >
              <AlertTriangle
                className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
                aria-hidden
              />
              {t('cade:audio.transcript.notice')}
            </p>
            {transcript.withheld > 0 ? (
              <p className="text-xs text-muted-foreground">
                {t('cade:audio.transcript.withheld', { count: transcript.withheld })}
              </p>
            ) : null}
            {transcript.recording.audio_url ? (
              // `preload="none"`: a session is hours of audio and most visits never press play.
              // No caption track: CADE publishes none, and this page IS the text alternative.
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <audio
                ref={audioRef}
                controls
                preload="none"
                src={transcript.recording.audio_url}
                className="sticky top-0 z-10 w-full bg-background"
              />
            ) : null}
            <Segments transcript={transcript} play={play} hash={location.hash} />
          </>
        )}
      </Answer>
    </div>
  );
}

function BackLink({ to, text }: { to: string; text: string }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 text-sm text-primary underline-offset-4 hover:underline"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
      {text}
    </Link>
  );
}

/** For each segment, the events that start on it and whether it lies inside any event. */
function eventIndex(events: CadeEventSpan[]) {
  const starts = new Map<number, CadeEventSpan[]>();
  const inside = new Set<number>();
  for (const event of events) {
    starts.set(event.seg_from, [...(starts.get(event.seg_from) ?? []), event]);
    for (let seg = event.seg_from; seg <= event.seg_to; seg += 1) inside.add(seg);
  }
  return { starts, inside };
}

function Segments({
  transcript,
  play,
  hash,
}: {
  transcript: CadeTranscript;
  play: (seconds: number) => void;
  hash: string;
}) {
  const { t } = useTranslation(['cade']);
  const index = useMemo(() => eventIndex(transcript.events), [transcript.events]);
  const [query, setQuery] = useState('');
  const [current, setCurrent] = useState(0);
  const terms = useMemo(() => findTerms(query), [query]);
  // Segment numbers holding every word typed, in the order they are spoken.
  const matches = useMemo(
    () => transcript.segments.filter((s) => holdsTerms(s.text, terms)).map((s) => s.seg),
    [transcript.segments, terms],
  );
  const matchSet = useMemo(() => new Set(matches), [matches]);
  const active = matches.length > 0 ? matches[Math.min(current, matches.length - 1)] : null;

  const jump = (position: number) => {
    if (matches.length === 0) return;
    const next = (position + matches.length) % matches.length;
    setCurrent(next);
    document.getElementById(`seg-${matches[next]}`)?.scrollIntoView({ block: 'center' });
  };

  if (transcript.segments.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('cade:audio.transcript.empty')}</p>;
  }
  return (
    <>
      <FindBar
        query={query}
        onQuery={(value) => {
          setQuery(value);
          setCurrent(0);
        }}
        count={matches.length}
        position={matches.length > 0 ? Math.min(current, matches.length - 1) : 0}
        onStep={(step) => jump(current + step)}
        searching={terms.length > 0}
      />
      <ol className="divide-y rounded-md border" data-transcript>
        {transcript.segments.map((segment) => {
          const id = `seg-${segment.seg}`;
          const marked = index.inside.has(segment.seg);
          const targeted = hash === `#${id}`;
          const found = matchSet.has(segment.seg);
          return (
            <li
              key={segment.seg}
              id={id}
              data-segment
              data-event={marked ? '' : undefined}
              data-match={found ? (segment.seg === active ? 'current' : '') : undefined}
              className={[
                'flex gap-3 p-2 text-sm',
                marked ? 'bg-primary/10' : '',
                targeted ? 'ring-2 ring-primary' : '',
                found ? 'border-l-4 border-amber-500' : '',
                segment.seg === active ? 'bg-amber-500/10' : '',
              ].join(' ')}
            >
              <button
                type="button"
                onClick={() => play(segment.start_s)}
                aria-label={t('cade:audio.transcript.play', { time: clock(segment.start_s) })}
                className="inline-flex h-6 shrink-0 items-center gap-1 rounded border px-1.5 text-xs text-foreground"
              >
                <Play className="h-3 w-3" aria-hidden />
                <span className="tabular-nums">{clock(segment.start_s)}</span>
              </button>
              <div className="space-y-1">
                {(index.starts.get(segment.seg) ?? []).map((event) => (
                  <span
                    key={event.event_type}
                    className="mr-1 inline-block rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-foreground"
                  >
                    {t(`cade:audio.transcript.event.${event.event_type}`)}
                  </span>
                ))}
                <p className="text-foreground">
                  {splitOnTerms(segment.text, found ? terms : []).map((part, at) =>
                    part.hit ? (
                      <mark key={at} className="rounded bg-amber-300/60 px-0.5 text-foreground">
                        {part.text}
                      </mark>
                    ) : (
                      part.text
                    ),
                  )}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    </>
  );
}

function FindBar({
  query,
  onQuery,
  count,
  position,
  onStep,
  searching,
}: {
  query: string;
  onQuery: (value: string) => void;
  count: number;
  position: number;
  onStep: (step: number) => void;
  searching: boolean;
}) {
  const { t } = useTranslation(['cade']);
  return (
    <div className="flex flex-wrap items-center gap-2" role="search">
      <label className="sr-only" htmlFor="transcript-find">
        {t('cade:audio.transcript.find')}
      </label>
      <input
        id="transcript-find"
        type="search"
        value={query}
        onChange={(event) => onQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') onStep(event.shiftKey ? -1 : 1);
        }}
        placeholder={t('cade:audio.transcript.find')}
        className="min-w-0 flex-1 rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
      />
      <button
        type="button"
        onClick={() => onStep(-1)}
        disabled={count === 0}
        aria-label={t('cade:audio.transcript.previous')}
        className="rounded-md border p-1.5 text-foreground disabled:opacity-50"
      >
        <ChevronUp className="h-4 w-4" aria-hidden />
      </button>
      <button
        type="button"
        onClick={() => onStep(1)}
        disabled={count === 0}
        aria-label={t('cade:audio.transcript.next')}
        className="rounded-md border p-1.5 text-foreground disabled:opacity-50"
      >
        <ChevronDown className="h-4 w-4" aria-hidden />
      </button>
      <span className="text-xs text-muted-foreground" role="status" data-find-count>
        {!searching
          ? ''
          : count === 0
            ? t('cade:audio.transcript.noMatches')
            : t('cade:audio.transcript.matches', { current: position + 1, count })}
      </span>
    </div>
  );
}
